<section class="bn-account-txns" data-testid="account-transactions">
  @if (store.loading()) {
    <div
      class="bn-account-txns__skeleton"
      data-testid="account-transactions-skeleton"
      role="status"
      aria-label="Loading transactions"
    >
      <p-skeleton width="12rem" height="2rem" />
      <p-skeleton width="100%" height="2.25rem" />
      <p-skeleton width="100%" height="2.25rem" />
    </div>
  } @else if (store.error()) {
    <div
      class="bn-account-txns__error"
      role="alert"
      data-testid="account-transactions-error"
    >
      <p>We couldn't load transactions right now.</p>
      <p-button
        label="Retry"
        severity="secondary"
        size="small"
        (onClick)="store.retry()"
      />
    </div>
  } @else {
    <div class="bn-account-txns__toolbar">
      @if (showSearch()) {
        <label class="bn-account-txns__field bn-account-txns__field--search">
          <span class="bn-account-txns__label">Search</span>
          <span class="bn-account-txns__search">
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              type="search"
              placeholder="Search transactions"
              aria-label="Search transactions"
              data-testid="account-transactions-search"
              [ngModel]="search()"
              (ngModelChange)="onSearchChange($event)"
            />
          </span>
        </label>
      }
      <div class="bn-account-txns__field bn-account-txns__field--date">
        <span class="bn-account-txns__label">Date</span>
        <p-select
          [options]="dateOptions"
          [ngModel]="datePreset()"
          (ngModelChange)="onDatePresetChange($event)"
          optionLabel="label"
          optionValue="value"
          ariaLabel="Filter by date"
          [appendTo]="'body'"
          size="small"
          styleClass="bn-account-txns__select"
          data-testid="account-transactions-date"
        />
      </div>
      @if (showCustomRange()) {
        <div class="bn-account-txns__field bn-account-txns__field--range">
          <label class="bn-account-txns__label" for="bn-account-txns-from"
            >From</label
          >
          <p-datepicker
            [ngModel]="customFrom()"
            (ngModelChange)="onCustomFromChange($event)"
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            [showIcon]="true"
            iconDisplay="input"
            [showButtonBar]="true"
            [appendTo]="'body'"
            inputId="bn-account-txns-from"
            styleClass="bn-account-txns__date"
          />
        </div>
        <div class="bn-account-txns__field bn-account-txns__field--range">
          <label class="bn-account-txns__label" for="bn-account-txns-to"
            >To</label
          >
          <p-datepicker
            [ngModel]="customTo()"
            (ngModelChange)="onCustomToChange($event)"
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            [showIcon]="true"
            iconDisplay="input"
            [showButtonBar]="true"
            [appendTo]="'body'"
            inputId="bn-account-txns-to"
            styleClass="bn-account-txns__date"
          />
        </div>
      }
      @if (showStatus()) {
        <div class="bn-account-txns__field bn-account-txns__field--status">
          <span class="bn-account-txns__label">Status</span>
          <p-select
            [options]="statusOptions()"
            [ngModel]="status()"
            (ngModelChange)="onStatusChange($event)"
            optionLabel="label"
            optionValue="value"
            ariaLabel="Filter by status"
            [appendTo]="'body'"
            size="small"
            styleClass="bn-account-txns__select"
            data-testid="account-transactions-status"
          />
        </div>
      }
    </div>

    @if (query().kind === 'invalid') {
      <p
        class="bn-account-txns__invalid"
        role="alert"
        data-testid="account-transactions-invalid"
      >
        End date must be after start date.
      </p>
    } @else if (query().kind === 'pending') {
      <p
        class="bn-account-txns__empty"
        data-testid="account-transactions-pending"
      >
        Choose a start and end date.
      </p>
    } @else if (paged().total === 0) {
      <p
        class="bn-account-txns__empty"
        data-testid="account-transactions-empty"
      >
        No transactions match these filters.
      </p>
    } @else {
      <table class="bn-account-txns__table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Status</th>
            <th class="bn-account-txns__amount-h">Amount</th>
          </tr>
        </thead>
        <tbody>
          @for (row of paged().rows; track transactionTrackKey(row)) {
            @if (isUnavailable(row)) {
              <tr class="bn-account-txns__unavailable" data-testid="account-transaction-unavailable">
                <td colspan="4">
                  <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                  Transaction details unavailable
                </td>
              </tr>
            } @else {
              <tr>
                <td class="bn-account-txns__date-cell">
                  {{ formatDate(row.createdDate) }}
                </td>
                <td class="bn-account-txns__desc">
                  {{ displayDescription(row.description) }}
                </td>
                <td>
                  <p-tag
                    [value]="row.status"
                    [severity]="statusSeverity(row.status)"
                    [rounded]="true"
                    styleClass="bn-accounts-status"
                  />
                </td>
                <td
                  class="bn-account-txns__amount"
                  [class.bn-account-txns__amount--in]="
                    formatAmount(row.amount).inflow
                  "
                >
                  {{ formatAmount(row.amount).text }}
                </td>
              </tr>
            }
          }
        </tbody>
      </table>

      <footer class="bn-account-txns__footer">
        <p class="bn-account-txns__count">
          Showing {{ paged().from }}–{{ paged().to }} of {{ paged().total }}
        </p>
        <div class="bn-account-txns__pager">
          <button
            type="button"
            class="bn-account-txns__page-btn"
            [disabled]="paged().page <= 1"
            (click)="prevPage()"
            data-testid="account-transactions-prev"
          >
            <i class="pi pi-chevron-left" aria-hidden="true"></i>
            Prev
          </button>
          <span>Page {{ paged().page }} of {{ paged().pageCount }}</span>
          <button
            type="button"
            class="bn-account-txns__page-btn"
            [disabled]="paged().page >= paged().pageCount"
            (click)="nextPage()"
            data-testid="account-transactions-next"
          >
            Next
            <i class="pi pi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </footer>
    }

    @if (query().kind === 'ready' && store.truncated() && !store.moreError()) {
      <p
        class="bn-account-txns__truncated"
        data-testid="account-transactions-truncated"
        role="status"
      >
        Showing the first {{ store.rows().length }} transactions. More history
        is available.
        <button
          type="button"
          class="bn-account-txns__page-btn"
          [disabled]="store.loadingMore()"
          (click)="store.loadMore()"
          data-testid="account-transactions-load-more"
        >
          Load more
        </button>
      </p>
    }
    @if (store.moreError()) {
      <p
        class="bn-account-txns__more-error"
        role="alert"
        data-testid="account-transactions-more-error"
      >
        We couldn't load more transactions.
        <button
          type="button"
          class="bn-account-txns__page-btn"
          [disabled]="store.loadingMore()"
          (click)="store.loadMore()"
          data-testid="account-transactions-retry-more"
        >
          Retry
        </button>
      </p>
    }
  }
</section>
